/**
 * FileImportPreview
 *
 * Handles:
 *   1. File drag-drop / browse (.csv, .xlsx)
 *   2. Upload to /api/patient-imports/upload → get validated session
 *   3. Preview table of validated rows with status tags
 *   4. "Import valid rows" → POST /api/patient-imports/{id}/commit
 */
import React, {useState} from 'react';
import {Alert, Button, Space, Table, Tag, Tooltip, Typography, Upload} from 'antd';
import {InboxOutlined} from '@ant-design/icons';
import type {RcFile, UploadProps} from 'antd/es/upload';
import {FormattedMessage, useIntl} from 'react-intl';
import {getPatientImportApi} from '../../api/apiInstances';
import {RowStatusTag, RowStatus} from './RowStatusTag';
import {useTrackEvent} from '../../hooks/useTrackEvent';

const {Dragger} = Upload;
const {Text} = Typography;

interface SessionRow {
    id: string;
    rowIndex: number;
    status: RowStatus;
    firstName?: string | null;
    secondName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    birthDate?: string | null;
    gender?: string | null;
    nationalId?: string | null;
    notes?: string | null;
    validationErrors?: Record<string, string[]> | null;
    duplicateInfo?: Array<{type: string; name?: string}> | null;
    createdPatientId?: string | null;
}

interface Session {
    id: string;
    status: string;
    source: string;
    originalFilename?: string | null;
    totalRows: number;
    validRows: number;
    committedRows: number;
    conflictRows: number;
    rows: SessionRow[];
}

interface CommitResult {
    committed: number;
    failed: number;
    skipped: number;
    rows: SessionRow[];
}

export const FileImportPreview: React.FC = () => {
    const intl = useIntl();
    const api = getPatientImportApi();

    const [uploading, setUploading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [session, setSession] = useState<Session | null>(null);
    const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const track = useTrackEvent();

    const IMPORT_MAX_BYTES = 5 * 1024 * 1024;

    const handleUpload = async (file: RcFile) => {
        setError(null);
        setSession(null);
        setCommitResult(null);

        if (file.size > IMPORT_MAX_BYTES) {
            setError(intl.formatMessage({id: 'import_file_too_large'}));
            return false;
        }

        setUploading(true);
        try {
            const resp = await api.uploadImportFile(file as File);
            setSession(resp.data as Session);
            track('patient_import_started', {
                import_method: 'file_upload',
                row_count: (resp.data as Session).totalRows ?? 0,
            });
        } catch (e: any) {
            const msg =
                e?.response?.data?.detail ||
                intl.formatMessage({id: 'import_upload_error'});
            setError(msg);
        } finally {
            setUploading(false);
        }
        return false; // prevent antd default upload
    };

    const handleCommit = async () => {
        if (!session) return;
        setError(null);
        setCommitting(true);
        try {
            const resp = await api.commitPatientImport(session.id);
            setCommitResult(resp.data as CommitResult);
            // Refresh session to show updated row statuses
            const refreshed = await api.getPatientImport(session.id);
            setSession(refreshed.data as Session);
        } catch (e: any) {
            const msg =
                e?.response?.data?.detail ||
                intl.formatMessage({id: 'import_commit_error'});
            setError(msg);
        } finally {
            setCommitting(false);
        }
    };

    const displayRows = commitResult ? commitResult.rows : session?.rows ?? [];

    const columns = [
        {
            title: '#',
            dataIndex: 'rowIndex',
            width: 50,
            render: (v: number) => <span style={{color: '#999', fontSize: 12}}>{v + 1}</span>,
        },
        {
            title: <FormattedMessage id="first_name"/>,
            dataIndex: 'firstName',
            width: 120,
        },
        {
            title: <FormattedMessage id="last_name"/>,
            dataIndex: 'lastName',
            width: 120,
        },
        {
            title: <FormattedMessage id="email"/>,
            dataIndex: 'email',
            width: 180,
        },
        {
            title: <FormattedMessage id="phone"/>,
            dataIndex: 'phone',
            width: 140,
        },
        {
            title: <FormattedMessage id="birth_date"/>,
            dataIndex: 'birthDate',
            width: 110,
        },
        {
            title: <FormattedMessage id="national_id_label"/>,
            dataIndex: 'nationalId',
            width: 110,
        },
        {
            title: <FormattedMessage id="status"/>,
            dataIndex: 'status',
            width: 140,
            render: (status: RowStatus, row: SessionRow) => (
                <RowStatusTag
                    status={status}
                    errors={row.validationErrors ?? undefined}
                    duplicateInfo={row.duplicateInfo ?? undefined}
                />
            ),
        },
    ];

    const commitableCount = session
        ? session.rows.filter(r => r.status === 'valid' || r.status === 'duplicate_warning').length
        : 0;

    const draggerProps: UploadProps = {
        name: 'file',
        multiple: false,
        accept: '.csv,.xlsx',
        showUploadList: false,
        beforeUpload: handleUpload,
    };

    return (
        <div>
            {!session && (
                <Dragger {...draggerProps} disabled={uploading} style={{marginBottom: 16}}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined/>
                    </p>
                    <p className="ant-upload-text">
                        <FormattedMessage id="import_file_upload_hint"/>
                    </p>
                    <p className="ant-upload-hint">
                        <FormattedMessage id="import_file_upload_formats"/>
                    </p>
                </Dragger>
            )}

            {uploading && (
                <Alert
                    type="info"
                    message={<FormattedMessage id="import_parsing_file"/>}
                    style={{marginBottom: 12}}
                />
            )}

            {error && (
                <Alert
                    type="error"
                    message={error}
                    closable
                    onClose={() => setError(null)}
                    style={{marginBottom: 12}}
                />
            )}

            {session && (
                <>
                    <Space style={{marginBottom: 12}} wrap>
                        <Text strong>{session.originalFilename}</Text>
                        <Tag color="blue">
                            <FormattedMessage
                                id="import_total_rows"
                                values={{count: session.totalRows}}
                            />
                        </Tag>
                        <Tag color="green">
                            <FormattedMessage
                                id="import_valid_rows"
                                values={{count: session.validRows}}
                            />
                        </Tag>
                        {session.conflictRows > 0 && (
                            <Tag color="orange">
                                <FormattedMessage
                                    id="import_warning_rows"
                                    values={{count: session.conflictRows}}
                                />
                            </Tag>
                        )}
                    </Space>

                    {commitResult && (
                        <Alert
                            type={commitResult.failed > 0 ? 'warning' : 'success'}
                            message={
                                <FormattedMessage
                                    id="import_commit_result"
                                    values={{
                                        committed: commitResult.committed,
                                        failed: commitResult.failed,
                                        skipped: commitResult.skipped,
                                    }}
                                />
                            }
                            style={{marginBottom: 12}}
                        />
                    )}

                    <Table
                        dataSource={displayRows}
                        columns={columns}
                        rowKey="id"
                        pagination={{pageSize: 50, showSizeChanger: false}}
                        size="small"
                        scroll={{x: 'max-content'}}
                        rowClassName={row => {
                            if (row.status === 'saved') return 'import-row-saved';
                            if (row.status === 'invalid' || row.status === 'failed')
                                return 'import-row-error';
                            if (row.status === 'conflict') return 'import-row-conflict';
                            if (row.status === 'duplicate_warning') return 'import-row-warning';
                            return '';
                        }}
                        style={{marginBottom: 12}}
                    />

                    <Space>
                        {!commitResult && commitableCount > 0 && (
                            <Button
                                type="primary"
                                loading={committing}
                                onClick={handleCommit}
                            >
                                <FormattedMessage
                                    id="import_commit_button"
                                    values={{count: commitableCount}}
                                />
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                setSession(null);
                                setCommitResult(null);
                                setError(null);
                            }}
                        >
                            <FormattedMessage id="import_upload_another"/>
                        </Button>
                    </Space>
                </>
            )}
        </div>
    );
};
